import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectError } from '../../redux-state/userAuth/selectors';
import PasswordInput from '../../components/common/PasswordInput';
import TextField from '@mui/material/TextField';
import { isLoggedIn } from '../../components/authProvider/AuthContext';
import PrimaryButton from '../../components/common/button/PrimaryButton';

function ChangePassword() {
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState("");
  const inputRefs = useRef([]);
  const error = useSelector(selectError);

  useEffect (() => {
    if(isLoggedIn()) {
      navigate('/dashboard')
    }
  });

  useEffect(() => {
    dispatch({ type: 'CLEAR_MFA_ERROR' });
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      setOtp(new Array(6).fill(''));
      inputRefs.current[0]?.focus();
      setLoading(false);
    }
  }, [error]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return passwordRegex.test(password);
  };

  const handleChange = (e, index) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, '');
    if (!inputValue) return;

    const newOtp = [...otp];

    newOtp[index] = inputValue[0];
    setOtp(newOtp);

    if (error) {
      dispatch({ type: 'CLEAR_MFA_ERROR' });
    }

    if (index < 5 && inputValue) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);

    if (paste.length === 0) return;

    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = paste[i] || '';
    }

    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length - 1, 5)]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];

      if (otp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setPasswordError('');
    dispatch({ type: 'CLEAR_MFA_ERROR' });

    let valid = true;

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      valid = false;
    }

    if (!validatePassword(password)) {
      setPasswordError(
        'Password must be strong, according to given instruction'
      );
      valid = false;
    }

    if (!valid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    changePassword();
  };

  const combinedOtp = otp.join('');

  const changePassword = () => {
    dispatch({
      type: 'CHANGE_PASSWORD',
      payload: {confirmation_code: combinedOtp, email: email, new_password: password },
      navigate: navigate,
    });
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  useEffect(() => {
    return () => {
      dispatch({ type: 'CLEAR_MFA_ERROR' });
    };
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <form className="bg-white rounded-2xl shadow-md p-10 min-w-[30%] w-[500px]" onSubmit={handleSubmit}>
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-12">
            <img className="h-14 w-14" src="/logo.png" alt="logo" />
            <span className="ml-3 text-lg font-semibold text-gray-900">
              DMV NAVIGATOR
            </span>
          </div>

          <div className='text-2xl text-gray-900 font-semibold mb-2'>
            Change Your password
          </div>

          <div className='text-gray-700'>
            Please enter your details
          </div>
        </div>

        <div className="mb-6">
          <div className="flex justify-center gap-3 mb-4">
            {otp.map((value, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength="1"
                value={value}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                className="w-14 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-md focus:outline-none focus:border-blue-500 transition-colors"
              />
            ))}
          </div>

          {error && (
            <div className="text-center text-sm text-red-500 mb-2">
              {error}
            </div>
          )}

          <div className="text-center">
            <span className="text-gray-600 text-sm">Didn't receive the verification code? </span>
          </div>
        </div>

        <div className="mb-4">
          <TextField
            required
            className="w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            id="outlined-required"
            label="Enter Work Email"
            error={!!emailError}
            helperText={emailError}
            InputProps={{ className: 'boarder-radius-10' }}
          />
        </div>

        <div className="mb-4">
          <PasswordInput
            className="w-full"
            password={password}
            handlePassword={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
          />
        </div>

        <div className='text-sm text-gray-600 mb-6'>
          Password must be 8 characters long with one special character, one number, one Upper case & one lower case.
        </div>

        <PrimaryButton
          text={loading ? 'Changing Password...' : 'Change Password'}
          type="submit"
          className="w-full"
          disabled={loading}
        />

        <div className="text-center mt-8">
          <button
            type="button"
            className="text-gray-600 text-sm hover:text-gray-800 hover:underline transition-colors"
            onClick={handleBackToLogin}
          >
            Back to Login
          </button>
        </div>
      </form>
    </div>
  )
}

export default ChangePassword
